import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  Check,
  Pencil,
  Users,
  X,
} from 'lucide-react';
import {
  Link,
  useNavigate,
  useParams,
} from 'react-router-dom';

import Navbar from '../components/Navbar';
import ProductSearch from '../components/cantina/ProductSearch';
import InventarioPinModal from '../components/cantina/productos/InventarioPinModal';
import CancelarPedidoModal from '../components/restaurante/mesa/CancelarPedidoModal';
import AnularPagoModal from '../components/restaurante/mesa/AnularPagoModal';
import BarraOperador from '../components/restaurante/BarraOperador';
import AvisoRestaurante from '../components/restaurante/AvisoRestaurante';
import useOperadorRestaurante from '../hooks/restaurante/useOperadorRestaurante';
import CuentaMesaPanel from '../components/restaurante/mesa/CuentaMesaPanel';
import PedidoMesaPanel from '../components/restaurante/mesa/PedidoMesaPanel';

import useCantinaProducts from '../hooks/cantina/useCantinaProducts';
import useRestauranteMesa from '../hooks/restaurante/useRestauranteMesa';
import useRestaurantePedidoCart from '../hooks/restaurante/useRestaurantePedidoCart';

import { api } from '../lib/api';

const money = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const estadoPedidoLabel = (estado) => {
  const labels = {
    ABIERTO: 'Preparando envío',
    CONFIRMADO: 'Enviado a cocina',
    EN_PREPARACION: 'En preparación',
    LISTO: 'Listo',
    ENTREGADO: 'Entregado',
    CANCELADO: 'Cancelado',
  };

  return labels[estado] || estado;
};

// Clave que identifica un intento de cobro: si se reenvía (doble clic o
// reintento por mala conexión), el servidor no lo registra dos veces.
const nuevaClaveCobro = () => {
  try {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (_) {
    // Se usa la alternativa de abajo.
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

// Misma regla que aplica el backend: se puede cancelar un pedido abierto,
// confirmado, en cocina (preparando o listo; el stock no vuelve) o, si todo
// fue de entrega directa, ya entregado.
const pedidoCancelable = (pedidoActual) => {
  if (
    ['ABIERTO', 'CONFIRMADO', 'EN_PREPARACION', 'LISTO'].includes(
      pedidoActual.estado
    )
  ) {
    return true;
  }

  const items = pedidoActual.items || [];

  return (
    pedidoActual.estado === 'ENTREGADO' &&
    items.length > 0 &&
    items.every(
      (item) => item.destino && item.destino !== 'COCINA'
    )
  );
};

export default function RestauranteMesa() {
  const { id } = useParams();
  const navigate = useNavigate();

  const restaurante = useRestauranteMesa(id);
  const pedido = useRestaurantePedidoCart();

  const productos = useCantinaProducts(pedido.agregar);

  const { operador, identificar } = useOperadorRestaurante();
  const [aviso, setAviso] = useState(null);

  const [pinAbierto, setPinAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [errorEnvio, setErrorEnvio] = useState('');

  const [editandoAlias, setEditandoAlias] = useState(false);
  const [aliasValor, setAliasValor] = useState('');
  const [guardandoAlias, setGuardandoAlias] = useState(false);
  const [errorAlias, setErrorAlias] = useState('');

  const [comensalesApertura, setComensalesApertura] = useState(1);
  const [pinAperturaAbierto, setPinAperturaAbierto] = useState(false);
  const [abriendoMesa, setAbriendoMesa] = useState(false);
  const [errorApertura, setErrorApertura] = useState('');

  const [editandoComensales, setEditandoComensales] =
    useState(false);
  const [comensalesValor, setComensalesValor] =
    useState(1);
  const [
    guardandoComensales,
    setGuardandoComensales,
  ] = useState(false);
  const [errorComensales, setErrorComensales] =
    useState('');

  const [cambiandoEstado, setCambiandoEstado] =
    useState(false);
  const [errorEstado, setErrorEstado] =
    useState('');

  const [cuentaDetalle, setCuentaDetalle] =
    useState(null);
  const [cargandoCuenta, setCargandoCuenta] =
    useState(false);
  const [errorCuenta, setErrorCuenta] =
    useState('');

  const [pagosPendientes, setPagosPendientes] =
    useState([]);
  const [
    aplicarPropinaPendiente,
    setAplicarPropinaPendiente,
  ] = useState(false);
  const [pinCobroAbierto, setPinCobroAbierto] =
    useState(false);
  const [procesandoCobro, setProcesandoCobro] =
    useState(false);
  const [errorCobro, setErrorCobro] =
    useState('');

  const [pedidoCancelar, setPedidoCancelar] =
    useState(null);
  const [cancelandoPedido, setCancelandoPedido] =
    useState(false);

  const [pagoAnular, setPagoAnular] = useState(null);
  const [anulandoPago, setAnulandoPago] = useState(false);

  const claveCobroRef = useRef(null);

  const cuentaOperacionId =
    restaurante.operacion?.cuentas?.[0]?.id || null;

  const cuentaOperacionTotal =
    restaurante.operacion?.cuentas?.[0]?.total ?? null;

  useEffect(() => {
    const cuentaId = cuentaOperacionId;

    if (!cuentaId) {
      setCuentaDetalle(null);
      setErrorCuenta('');
      setCargandoCuenta(false);
      return;
    }

    let activo = true;

    const cargarCuenta = async () => {
      try {
        setCargandoCuenta(true);
        setErrorCuenta('');

        const response = await api.get(
          `/restaurante/cuentas/${cuentaId}`
        );

        if (activo) {
          setCuentaDetalle(response.data || null);
        }
      } catch (error) {
        console.error(
          'Error cargando cuenta Restaurante:',
          error
        );

        if (activo) {
          setCuentaDetalle(null);
          setErrorCuenta(
            error?.response?.data?.error ||
              'No se pudo cargar la cuenta.'
          );
        }
      } finally {
        if (activo) {
          setCargandoCuenta(false);
        }
      }
    };

    cargarCuenta();

    return () => {
      activo = false;
    };
  }, [
    cuentaOperacionId,
    cuentaOperacionTotal,
  ]);

  const solicitarCobro = (
    pagos,
    aplicarPropina = false
  ) => {
    if (
      procesandoCobro ||
      !Array.isArray(pagos) ||
      !pagos.length
    ) {
      return;
    }

    claveCobroRef.current = nuevaClaveCobro();
    setPagosPendientes(pagos);
    setAplicarPropinaPendiente(
      aplicarPropina === true
    );
    setErrorCobro('');
    setMensaje('');
    setPinCobroAbierto(true);
  };

  const confirmarCobro = async (usuario) => {
    if (
      !usuario?.id ||
      procesandoCobro ||
      !cuentaOperacionId ||
      !pagosPendientes.length
    ) {
      return;
    }

    setProcesandoCobro(true);
    setErrorCobro('');
    setMensaje('');

    try {
      const response = await api.post(
        `/restaurante/cuentas/${cuentaOperacionId}/pagar`,
        {
          usuario_id: usuario.id,
          pagos: pagosPendientes,
          aplicar_propina: aplicarPropinaPendiente,
          clave_idempotencia: claveCobroRef.current,
        }
      );

      const resultado = response.data || {};

      claveCobroRef.current = null;

      setPinCobroAbierto(false);
      setPagosPendientes([]);
      setAplicarPropinaPendiente(false);

      if (resultado.mesa_liberada) {
        navigate('/restaurante', {
          replace: true,
          state: {
            aviso: `Cuenta de ${mesa.nombre} cobrada y mesa liberada.`,
          },
        });
        return;
      }

      const cuentaResponse = await api.get(
        `/restaurante/cuentas/${cuentaOperacionId}`
      );

      setCuentaDetalle(
        cuentaResponse.data || null
      );

      await restaurante.recargar();

      const saldo = Number(
        resultado.saldo || 0
      );

      const textoCobro =
        saldo > 0
          ? `Pago registrado. Saldo pendiente: ${money(
              saldo
            )}.`
          : 'Pago registrado correctamente.';

      setMensaje(textoCobro);
      setAviso({ tipo: 'ok', mensaje: textoCobro });
    } catch (error) {
      console.error(
        'Error cobrando cuenta Restaurante:',
        error
      );

      setPinCobroAbierto(false);

      const textoErrorCobro =
        error?.response?.data?.error ||
        'No se pudo registrar el pago.';

      setErrorCobro(textoErrorCobro);
      setAviso({ tipo: 'error', mensaje: textoErrorCobro });

      try {
        const cuentaResponse = await api.get(
          `/restaurante/cuentas/${cuentaOperacionId}`
        );

        setCuentaDetalle(
          cuentaResponse.data || null
        );
      } catch (_) {
        // Conservamos el error original del cobro.
      }
    } finally {
      setProcesandoCobro(false);
    }
  };

  const solicitarEnvio = () => {
    if (!pedido.items.length || enviando) return;

    setErrorEnvio('');
    setMensaje('');

    if (operador) {
      confirmarEnvio(operador);
      return;
    }

    setPinAbierto(true);
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (
        event.ctrlKey &&
        event.key === 'Enter' &&
        pedido.items.length &&
        !enviando &&
        !pinAbierto
      ) {
        event.preventDefault();
        solicitarEnvio();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  });

  const iniciarEdicionAlias = () => {
    const aliasActual =
      restaurante.operacion?.mesa?.alias || '';

    setAliasValor(aliasActual);
    setErrorAlias('');
    setEditandoAlias(true);
  };

  const cancelarEdicionAlias = () => {
    setEditandoAlias(false);
    setErrorAlias('');
  };

  const guardarAlias = async () => {
    if (guardandoAlias) return;

    const alias = String(aliasValor || '').trim();

    if (alias.length > 80) {
      setErrorAlias(
        'El apodo no puede superar 80 caracteres.'
      );
      return;
    }

    setGuardandoAlias(true);
    setErrorAlias('');

    try {
      await api.patch(
        `/restaurante/mesas/${id}`,
        { alias }
      );

      await restaurante.recargar();
      setEditandoAlias(false);
    } catch (error) {
      console.error(
        'Error guardando apodo de mesa:',
        error
      );

      setErrorAlias(
        error?.response?.data?.error ||
          'No se pudo guardar el apodo.'
      );
    } finally {
      setGuardandoAlias(false);
    }
  };

  const solicitarAperturaMesa = () => {
    const cantidad = Number(comensalesApertura);

    if (
      !Number.isInteger(cantidad) ||
      cantidad < 1 ||
      abriendoMesa
    ) {
      setErrorApertura(
        'Ingresá una cantidad válida de comensales.'
      );
      return;
    }

    setErrorApertura('');

    // Con un operador identificado no se vuelve a pedir el PIN.
    if (operador) {
      confirmarAperturaMesa(operador);
      return;
    }

    setPinAperturaAbierto(true);
  };

  const confirmarAperturaMesa = async (usuario) => {
    if (!usuario?.id || abriendoMesa) {
      return;
    }

    const cantidad = Number(comensalesApertura);

    if (
      !Number.isInteger(cantidad) ||
      cantidad < 1
    ) {
      setPinAperturaAbierto(false);
      setErrorApertura(
        'Ingresá una cantidad válida de comensales.'
      );
      return;
    }

    setAbriendoMesa(true);
    setErrorApertura('');

    try {
      await api.post(
        `/restaurante/mesas/${id}/abrir`,
        {
          usuario_id: usuario.id,
          comensales: cantidad,
        }
      );

      setPinAperturaAbierto(false);
      await restaurante.recargar();

      setMensaje(
        `Mesa abierta para ${cantidad} ${
          cantidad === 1
            ? 'comensal'
            : 'comensales'
        }.`
      );

      setTimeout(() => {
        productos.focusSearch();
      }, 0);
    } catch (error) {
      console.error(
        'Error abriendo mesa:',
        error
      );

      setPinAperturaAbierto(false);

      setErrorApertura(
        error?.response?.data?.error ||
          'No se pudo abrir la mesa.'
      );

      await restaurante.recargar();
    } finally {
      setAbriendoMesa(false);
    }
  };

  const iniciarEdicionComensales = () => {
    const actuales = Number(
      restaurante.operacion?.sesion?.comensales || 1
    );

    setComensalesValor(actuales);
    setErrorComensales('');
    setEditandoComensales(true);
  };

  const cancelarEdicionComensales = () => {
    if (guardandoComensales) return;

    setEditandoComensales(false);
    setErrorComensales('');
  };

  const guardarComensales = async () => {
    const cantidad = Number(comensalesValor);

    if (
      !Number.isInteger(cantidad) ||
      cantidad < 1 ||
      guardandoComensales
    ) {
      setErrorComensales(
        'Ingresá una cantidad válida de comensales.'
      );
      return;
    }

    setGuardandoComensales(true);
    setErrorComensales('');
    setMensaje('');

    try {
      await api.patch(
        `/restaurante/mesas/${id}/comensales`,
        {
          comensales: cantidad,
        }
      );

      await restaurante.recargar();

      setEditandoComensales(false);

      setMensaje(
        `Comensales actualizados a ${cantidad}.`
      );
    } catch (error) {
      console.error(
        'Error actualizando comensales:',
        error
      );

      setErrorComensales(
        error?.response?.data?.error ||
          'No se pudieron actualizar los comensales.'
      );

      await restaurante.recargar();
    } finally {
      setGuardandoComensales(false);
    }
  };

  const cambiarEstadoMesa = async (estado) => {
    if (cambiandoEstado) return;

    setCambiandoEstado(true);
    setErrorEstado('');
    setMensaje('');

    try {
      await api.patch(
        `/restaurante/mesas/${id}`,
        { estado }
      );

      await restaurante.recargar();

      setMensaje(
        estado === 'FUERA_DE_SERVICIO'
          ? 'Mesa puesta fuera de servicio.'
          : 'Mesa habilitada nuevamente.'
      );
    } catch (error) {
      console.error(
        'Error cambiando estado de mesa:',
        error
      );

      setErrorEstado(
        error?.response?.data?.error ||
          'No se pudo cambiar el estado de la mesa.'
      );

      await restaurante.recargar();
    } finally {
      setCambiandoEstado(false);
    }
  };

  const confirmarEnvio = async (usuario) => {
    if (!usuario?.id) return;

    const operacion = restaurante.operacion;

    const sesion = operacion?.sesion;
    const cuenta = operacion?.cuentas?.[0];

    if (!sesion || !cuenta) {
      setPinAbierto(false);
      setErrorEnvio(
        'La mesa ya no tiene una sesión o cuenta abierta.'
      );
      return;
    }

    setEnviando(true);
    setErrorEnvio('');
    setMensaje('');

    try {
      const response = await api.post(
        '/restaurante/pedidos/enviar',
        {
          sesion_id: sesion.id,
          cuenta_id: cuenta.id,
          usuario_id: usuario.id,

          items: pedido.items.map((item) => ({
            producto_id: item.id,
            cantidad: Number(item.cantidad),
            observaciones:
              String(item.observaciones || '').trim() ||
              null,
          })),
        }
      );

      const pedidoCreado = response.data?.pedido;

      pedido.vaciar();
      setPinAbierto(false);

      await restaurante.recargar();

      setMensaje(
        pedidoCreado?.id
          ? `Pedido #${pedidoCreado.id} enviado a Cocina.`
          : 'Pedido enviado a Cocina.'
      );

      productos.limpiarBusqueda();
      productos.focusSearch();
    } catch (error) {
      const data = error?.response?.data;

      let mensajeError =
        data?.error ||
        'No se pudo enviar el pedido a Cocina.';

      if (
        data?.error === 'Stock insuficiente' &&
        data?.producto?.nombre
      ) {
        mensajeError =
          `Stock insuficiente de ${data.producto.nombre}. ` +
          `Disponible: ${data.producto.stock_disponible}.`;
      }

      setErrorEnvio(mensajeError);
      setPinAbierto(false);

      productos.focusSearch();
    } finally {
      setEnviando(false);
    }
  };

  const confirmarAnulacionPago = async ({
    pagoId,
    autorizacionPin,
    motivo,
  }) => {
    setAnulandoPago(true);
    setMensaje('');
    setErrorCobro('');

    try {
      await api.post(`/restaurante/pagos/${pagoId}/anular`, {
        autorizacionPin,
        motivo,
      });

      setPagoAnular(null);

      if (cuentaOperacionId) {
        const cuentaResponse = await api.get(
          `/restaurante/cuentas/${cuentaOperacionId}`
        );

        setCuentaDetalle(cuentaResponse.data || null);
      }

      await restaurante.recargar();

      setMensaje('Pago anulado. El monto volvió al saldo de la cuenta.');

      return { ok: true };
    } catch (error) {
      console.error('Error anulando pago Restaurante:', error);

      return {
        ok: false,
        error:
          error?.response?.data?.error ||
          'No se pudo anular el pago.',
      };
    } finally {
      setAnulandoPago(false);
    }
  };

  const confirmarCancelacionPedido = async ({
    pedidoId,
    autorizacionPin,
    motivo,
  }) => {
    setCancelandoPedido(true);
    setMensaje('');
    setErrorEnvio('');

    try {
      await api.post(
        `/restaurante/pedidos/${pedidoId}/cancelar`,
        {
          autorizacionPin,
          motivo,
        }
      );

      setPedidoCancelar(null);

      await restaurante.recargar();

      setMensaje(`Pedido #${pedidoId} cancelado.`);

      return { ok: true };
    } catch (error) {
      console.error(
        'Error cancelando pedido Restaurante:',
        error
      );

      return {
        ok: false,
        error:
          error?.response?.data?.error ||
          'No se pudo cancelar el pedido.',
      };
    } finally {
      setCancelandoPedido(false);
    }
  };

  if (restaurante.cargando) {
    return (
      <>
        <Navbar />

        <main className="min-h-screen bg-gray-50 p-6">
          <div className="mx-auto max-w-7xl rounded-2xl bg-white p-10 text-center text-gray-500 shadow-sm">
            Cargando mesa...
          </div>
        </main>
      </>
    );
  }

  if (restaurante.error || !restaurante.operacion) {
    return (
      <>
        <Navbar />

        <main className="min-h-screen bg-gray-50 p-6">
          <div className="mx-auto max-w-7xl rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
            <p className="font-bold text-red-700">
              {restaurante.error ||
                'No se pudo cargar la mesa.'}
            </p>

            <Link
              to="/restaurante"
              className="mt-5 inline-block rounded-xl bg-azul-oscuro px-5 py-3 font-bold text-white"
            >
              Volver al salón
            </Link>
          </div>
        </main>
      </>
    );
  }

  const { mesa, sesion, cuentas, pedidos } =
    restaurante.operacion;

  const cuentaPrincipal = cuentas?.[0] || null;

  const pedidosActivos = (pedidos || []).filter(
    (pedidoActual) =>
      pedidoActual.estado !== 'CANCELADO'
  );

  return (
    <>
      <Navbar />

      <AvisoRestaurante
        mensaje={aviso?.mensaje}
        tipo={aviso?.tipo}
        onCerrar={() => setAviso(null)}
      />

      <main className="min-h-screen bg-gray-50 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link
              to="/restaurante"
              className="inline-flex items-center gap-2 text-sm font-bold text-azul-oscuro hover:text-verde-esmeralda"
            >
              <ArrowLeft size={18} />
              Volver al salón
            </Link>

            <BarraOperador
              operador={operador}
              onIdentificar={identificar}
            />
          </div>

          <section className="mt-4 overflow-hidden rounded-2xl bg-gradient-to-r from-azul-oscuro to-verde-esmeralda p-6 text-white shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-amarillo-dorado">
                  Restaurante
                </p>

                <div className="mt-1 flex flex-wrap items-center gap-3">
                  <h1 className="text-3xl font-extrabold">
                    {mesa.nombre}
                  </h1>

                  {!editandoAlias && (
                    <button
                      type="button"
                      onClick={iniciarEdicionAlias}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-white transition hover:bg-white/25"
                      title={
                        mesa.alias
                          ? 'Editar apodo'
                          : 'Agregar apodo'
                      }
                    >
                      <Pencil size={13} />
                      {mesa.alias || 'Agregar apodo'}
                    </button>
                  )}
                </div>

                {editandoAlias && (
                  <div className="mt-3 max-w-sm">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={aliasValor}
                        onChange={(event) =>
                          setAliasValor(event.target.value)
                        }
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            guardarAlias();
                          }

                          if (event.key === 'Escape') {
                            cancelarEdicionAlias();
                          }
                        }}
                        maxLength={80}
                        autoFocus
                        placeholder="Ej. Ventana"
                        className="min-w-0 flex-1 rounded-lg border border-white/30 bg-white px-3 py-2 text-sm font-semibold text-azul-oscuro outline-none ring-0 placeholder:text-gray-400"
                      />

                      <button
                        type="button"
                        onClick={guardarAlias}
                        disabled={guardandoAlias}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-amarillo-dorado text-azul-oscuro transition hover:brightness-105 disabled:opacity-60"
                        title="Guardar apodo"
                      >
                        <Check size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={cancelarEdicionAlias}
                        disabled={guardandoAlias}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white transition hover:bg-white/25 disabled:opacity-60"
                        title="Cancelar"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    {errorAlias && (
                      <p className="mt-2 text-xs font-bold text-red-100">
                        {errorAlias}
                      </p>
                    )}

                    <p className="mt-1 text-xs text-white/60">
                      Enter guarda · Esc cancela · vacío elimina el apodo
                    </p>
                  </div>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-white/80">
                  <Users size={17} />

                  {sesion ? (
                    <>
                      {!editandoComensales ? (
                        <>
                          <span>
                            {sesion.comensales}{' '}
                            {Number(sesion.comensales) === 1
                              ? 'comensal'
                              : 'comensales'}
                          </span>

                          <button
                            type="button"
                            onClick={iniciarEdicionComensales}
                            className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1 text-xs font-bold text-white transition hover:bg-white/20"
                          >
                            <Pencil size={13} />
                            Editar
                          </button>
                        </>
                      ) : (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setComensalesValor((actual) =>
                                Math.max(
                                  1,
                                  Number(actual || 1) - 1
                                )
                              )
                            }
                            disabled={guardandoComensales}
                            className="h-8 w-8 rounded-lg bg-white/15 text-lg font-extrabold text-white transition hover:bg-white/25 disabled:opacity-50"
                          >
                            −
                          </button>

                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={comensalesValor}
                            onChange={(event) => {
                              setComensalesValor(
                                event.target.value
                              );
                              setErrorComensales('');
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                guardarComensales();
                              }

                              if (event.key === 'Escape') {
                                cancelarEdicionComensales();
                              }
                            }}
                            autoFocus
                            className="h-8 w-16 rounded-lg border border-white/30 bg-white px-2 text-center text-sm font-extrabold text-azul-oscuro outline-none"
                          />

                          <button
                            type="button"
                            onClick={() =>
                              setComensalesValor((actual) =>
                                Number(actual || 0) + 1
                              )
                            }
                            disabled={guardandoComensales}
                            className="h-8 w-8 rounded-lg bg-white/15 text-lg font-extrabold text-white transition hover:bg-white/25 disabled:opacity-50"
                          >
                            +
                          </button>

                          <button
                            type="button"
                            onClick={guardarComensales}
                            disabled={guardandoComensales}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amarillo-dorado text-azul-oscuro disabled:opacity-50"
                            title="Guardar comensales"
                          >
                            <Check size={16} />
                          </button>

                          <button
                            type="button"
                            onClick={cancelarEdicionComensales}
                            disabled={guardandoComensales}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/15 text-white transition hover:bg-white/25 disabled:opacity-50"
                            title="Cancelar"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    `${mesa.capacidad} personas de capacidad`
                  )}
                </div>

                {errorComensales && (
                  <p className="mt-2 text-xs font-bold text-red-100">
                    {errorComensales}
                  </p>
                )}
              </div>

              <div className="text-right">
                <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-bold">
                  {mesa.estado === 'OCUPADA'
                    ? 'Mesa ocupada'
                    : mesa.estado === 'FUERA_DE_SERVICIO'
                      ? 'Fuera de servicio'
                      : 'Mesa libre'}
                </span>

                {cuentaPrincipal && (
                  <div className="mt-3">
                    <p className="text-xs text-white/70">
                      Cuenta actual
                    </p>

                    <strong className="text-3xl">
                      {money(cuentaPrincipal.total)}
                    </strong>
                  </div>
                )}
              </div>
            </div>
          </section>

          {!sesion && mesa.estado === 'FUERA_DE_SERVICIO' ? (
            <section className="mt-6 rounded-2xl border border-amber-200 bg-white p-8 shadow-sm">
              <div className="mx-auto max-w-md text-center">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-amber-600">
                  Fuera de servicio
                </p>

                <h2 className="mt-2 text-2xl font-extrabold text-azul-oscuro">
                  {mesa.nombre} no está disponible
                </h2>

                {mesa.alias && (
                  <p className="mt-1 text-sm font-bold text-verde-esmeralda">
                    {mesa.alias}
                  </p>
                )}

                <p className="mt-3 text-sm text-gray-500">
                  Esta mesa no puede abrirse hasta volver a habilitarla.
                </p>

                {errorEstado && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                    {errorEstado}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() =>
                    cambiarEstadoMesa('LIBRE')
                  }
                  disabled={cambiandoEstado}
                  className="mt-6 w-full rounded-xl bg-verde-esmeralda px-5 py-3 text-base font-extrabold text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {cambiandoEstado
                    ? 'Habilitando...'
                    : 'Habilitar mesa'}
                </button>
              </div>
            </section>
          ) : !sesion ? (
            <section className="mt-6 rounded-2xl border border-emerald-200 bg-white p-8 shadow-sm">
              <div className="mx-auto max-w-md text-center">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-verde-esmeralda">
                  Mesa disponible
                </p>

                <h2 className="mt-2 text-2xl font-extrabold text-azul-oscuro">
                  Abrir {mesa.nombre}
                </h2>

                {mesa.alias && (
                  <p className="mt-1 text-sm font-bold text-verde-esmeralda">
                    {mesa.alias}
                  </p>
                )}

                <p className="mt-3 text-sm text-gray-500">
                  Indicá cuántas personas se van a sentar.
                </p>

                <div className="mt-6">
                  <label
                    htmlFor="comensales-apertura"
                    className="mb-2 block text-left text-sm font-bold text-gray-700"
                  >
                    Cantidad de comensales
                  </label>

                  <div className="flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setComensalesApertura((actual) =>
                          Math.max(
                            1,
                            Number(actual || 1) - 1
                          )
                        )
                      }
                      className="h-12 w-12 rounded-xl border border-gray-200 bg-gray-50 text-2xl font-extrabold text-azul-oscuro transition hover:bg-gray-100"
                    >
                      −
                    </button>

                    <input
                      id="comensales-apertura"
                      type="number"
                      min="1"
                      step="1"
                      value={comensalesApertura}
                      onChange={(event) => {
                        setComensalesApertura(
                          event.target.value
                        );
                        setErrorApertura('');
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          solicitarAperturaMesa();
                        }
                      }}
                      className="h-12 w-24 rounded-xl border border-gray-200 bg-white text-center text-xl font-extrabold text-azul-oscuro outline-none focus:border-verde-esmeralda"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setComensalesApertura((actual) =>
                          Number(actual || 0) + 1
                        )
                      }
                      className="h-12 w-12 rounded-xl border border-gray-200 bg-gray-50 text-2xl font-extrabold text-azul-oscuro transition hover:bg-gray-100"
                    >
                      +
                    </button>
                  </div>
                </div>

                {errorApertura && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                    {errorApertura}
                  </div>
                )}

                <button
                  type="button"
                  onClick={solicitarAperturaMesa}
                  disabled={abriendoMesa}
                  className="mt-6 w-full rounded-xl bg-verde-esmeralda px-5 py-3 text-base font-extrabold text-white transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {abriendoMesa
                    ? 'Abriendo mesa...'
                    : 'Abrir mesa'}
                </button>

                <p className="mt-2 text-xs text-gray-400">
                  Enter continúa · luego se solicitará el PIN del operador
                </p>

                <div className="my-6 border-t border-gray-100" />

                {errorEstado && (
                  <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                    {errorEstado}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() =>
                    cambiarEstadoMesa(
                      'FUERA_DE_SERVICIO'
                    )
                  }
                  disabled={cambiandoEstado}
                  className="w-full rounded-xl border border-amber-300 bg-amber-50 px-5 py-3 text-sm font-extrabold text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {cambiandoEstado
                    ? 'Actualizando...'
                    : 'Poner fuera de servicio'}
                </button>
              </div>
            </section>
          ) : (
            <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
              <section className="min-w-0 rounded-2xl bg-white p-6 shadow-sm">
                <div>
                  <h2 className="text-xl font-extrabold text-azul-oscuro">
                    Nuevo pedido
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    Agregá productos y enviá la comanda a Cocina.
                  </p>
                </div>

                {mensaje && (
                  <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
                    {mensaje}
                  </div>
                )}

                {errorEnvio && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                    {errorEnvio}
                  </div>
                )}

                <ProductSearch
                  inputRef={productos.inputRef}
                  busqueda={productos.busqueda}
                  setBusqueda={productos.setBusqueda}
                  resultados={productos.resultados}
                  seleccion={productos.seleccion}
                  cargando={productos.cargando}
                  onKeyDown={productos.onKeyDown}
                  onAdd={productos.agregarSeleccionado}
                />

                <PedidoMesaPanel
                  items={pedido.items}
                  total={pedido.total}
                  unidades={pedido.unidades}
                  enviando={enviando}
                  onCantidad={pedido.cambiarCantidad}
                  onObservaciones={
                    pedido.cambiarObservaciones
                  }
                  onQuitar={pedido.quitar}
                  onEnviar={solicitarEnvio}
                />
              </section>

              <aside className="space-y-6">
                <CuentaMesaPanel
                  key={`${cuentaDetalle?.id || 'sin-cuenta'}-${
                    cuentaDetalle?.resumen?.pagado || 0
                  }`}
                  cuenta={cuentaDetalle}
                  cargando={cargandoCuenta}
                  procesando={procesandoCobro}
                  error={errorCobro || errorCuenta}
                  onCobrar={solicitarCobro}
                  onAnularPago={setPagoAnular}
                  mesa={mesa}
                  sesion={sesion}
                  pedidos={pedidosActivos}
                />

                <section className="rounded-2xl bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <h2 className="font-extrabold text-azul-oscuro">
                    Consumo
                  </h2>

                  <span className="text-xs font-bold text-gray-500">
                    {pedidosActivos.length}{' '}
                    {pedidosActivos.length === 1
                      ? 'pedido'
                      : 'pedidos'}
                  </span>
                </div>

                <div className="mt-4 space-y-3">
                  {pedidosActivos.map((pedidoActual) => (
                    <div
                      key={pedidoActual.id}
                      className="rounded-xl border border-gray-200 p-3"
                    >
                      <div className="flex justify-between gap-3">
                        <div>
                          <strong className="text-sm text-azul-oscuro">
                            Pedido #{pedidoActual.id}
                          </strong>

                          <p className="mt-0.5 text-xs font-semibold text-gray-500">
                            {estadoPedidoLabel(pedidoActual.estado)}
                          </p>
                        </div>

                        <strong className="text-sm text-verde-oscuro">
                          {money(pedidoActual.total)}
                        </strong>
                      </div>

                      <div className="mt-2 space-y-1">
                        {pedidoActual.items.map((item) => (
                          <div
                            key={item.id}
                            className="text-xs text-gray-600"
                          >
                            <div className="flex justify-between gap-3">
                              <span>
                                {item.cantidad} ×{' '}
                                {item.producto_nombre}
                              </span>

                              <span>
                                {money(item.subtotal)}
                              </span>
                            </div>

                            {item.observaciones && (
                              <p className="mt-1 italic text-gray-400">
                                {item.observaciones}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>

                      {pedidoCancelable(pedidoActual) && (
                        <div className="mt-3 flex justify-end border-t border-gray-100 pt-2">
                          <button
                            type="button"
                            onClick={() =>
                              setPedidoCancelar(pedidoActual)
                            }
                            disabled={cancelandoPedido}
                            className="rounded-lg px-2.5 py-1 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                          >
                            Cancelar pedido
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {!pedidosActivos.length && (
                    <div className="rounded-xl bg-slate-50 p-5 text-center text-sm text-slate-400">
                      Sin consumos todavía.
                    </div>
                  )}
                </div>
                </section>
              </aside>
            </div>
          )}
        </div>
      </main>

      <InventarioPinModal
        abierto={pinAperturaAbierto}
        titulo={`Abrir mesa · ${mesa.nombre}`}
        procesando={abriendoMesa}
        onClose={() => {
          if (!abriendoMesa) {
            setPinAperturaAbierto(false);
          }
        }}
        onConfirm={(usuario) => {
          identificar(usuario);
          return confirmarAperturaMesa(usuario);
        }}
      />

      <InventarioPinModal
        abierto={pinAbierto}
        titulo={`Enviar comanda · ${mesa.nombre}`}
        procesando={enviando}
        onClose={() => {
          if (!enviando) {
            setPinAbierto(false);
            productos.focusSearch();
          }
        }}
        onConfirm={(usuario) => {
          identificar(usuario);
          return confirmarEnvio(usuario);
        }}
      />

      <InventarioPinModal
        abierto={pinCobroAbierto}
        titulo={`Cobrar cuenta · ${mesa.nombre}`}
        procesando={procesandoCobro}
        onClose={() => {
          if (!procesandoCobro) {
            setPinCobroAbierto(false);
            setPagosPendientes([]);
            setAplicarPropinaPendiente(false);
          }
        }}
        onConfirm={confirmarCobro}
      />

      <CancelarPedidoModal
        abierto={Boolean(pedidoCancelar)}
        pedido={pedidoCancelar}
        procesando={cancelandoPedido}
        onClose={() => {
          if (!cancelandoPedido) {
            setPedidoCancelar(null);
          }
        }}
        onConfirm={confirmarCancelacionPedido}
      />

      <AnularPagoModal
        abierto={Boolean(pagoAnular)}
        pago={pagoAnular}
        procesando={anulandoPago}
        onClose={() => {
          if (!anulandoPago) {
            setPagoAnular(null);
          }
        }}
        onConfirm={confirmarAnulacionPago}
      />
    </>
  );
}
